import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import { Checkbox, TextField, Typography } from '@mui/material';
import { ReactComponent as DownloadPDFIcon } from 'assets/icons/downloadpdf.svg';
import { ReactComponent as DontHaveHistoricImage } from 'assets/images/FieldNotebook/dont_have_historics.svg';
import { ReactComponent as DontHaveArchivesImage } from 'assets/images/FieldNotebook/network_error.svg';
import { themeOptions } from 'assets/styles/theme';
import { Modal, ImperativeModalHandler } from 'components/Modal/Modal';
import { getPdfTemplateConfig } from 'config/firebase';
import { TimelineItemSkeleton } from 'components/SekeletonLoading/TimelineItemSkeleton/TimelineItemSkeleton';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { TrackingService } from 'services/trackingService';
import { ReduxState } from 'store';
import { getArchivesRequest } from 'store/states/archives/actions';
import { Archive } from 'store/states/archives/types';
import { setAnswers } from 'store/states/forms/actions';
import { FormState } from 'store/states/forms/types';
import {
  clearHistoric,
  getHistoricsRequest,
  setSelectedHistoric,
} from 'store/states/historics/actions';
import { Historic, HistoricState } from 'store/states/historics/types';
import { getOrganizationRequest } from 'store/states/organization/actionts';
import { OrganizationState } from 'store/states/organization/types';
import { formatTimestampDate } from 'utils/fieldNotebookUtils/formatters';
import {
  buildCombinedPdfName,
  buildIndividualPdfName,
  sanitizeFileName,
} from 'utils/fieldNotebookUtils/pdfFileName';
import {
  generateAnswerPdf,
  generateCombinedAnswerPdf,
} from 'utils/fieldNotebookUtils/pdfUtils';
import { HistoricExportButton } from './HistoricExportButton';
import { Button } from '../components/Button/Button';
import { Container } from '../components/Container/Container';
import { HistoricFilter } from '../components/HistoricFilter/HistoricFilter';
import { SearchBar } from '../components/SearchBar/SearchBar';
import { SubTitle } from '../components/SubTitle/SubTitle';
import { Title } from '../components/Title/Title';
import { HistoricContent } from './HistoricContent';
import {
  Date,
  Divider,
  DownloadPDFButton,
  DontHaveArchivesContainer,
  DontHaveHistoricsContainer,
  EditedText,
  NameModalContent,
  SelectableTile,
  SelectionBar,
  Timeline,
  TimelineConnector,
  TimelineItem,
  ToolsBar,
} from './styles';

export function AnswersPage() {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const loaderRef = useRef(null);

  const handleLoadingArchives = () => {
    dispatch(getArchivesRequest());
    dispatch(getOrganizationRequest());
  };

  const currentLanguage = i18n.language;
  const { id: archiveId } = useSelector<ReduxState, Archive>(
    (state) => state.archives.selectedArchive,
  );
  const { organization } = useSelector<ReduxState, OrganizationState>(
    (state) => state.organization,
  );
  const { historics, loading, hasNextPage, error } = useSelector<
    ReduxState,
    HistoricState
  >((state) => state.historics);
  const { selectedForm } = useSelector<ReduxState, FormState>(
    (state) => state.forms,
  );
  const hasHistorics = historics.length > 0;

  // Modo selecao para exportar PDF (individual ou combinado).
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedIndexes, setSelectedIndexes] = useState<number[]>([]);
  const [isExporting, setIsExporting] = useState(false);

  // Modal de nome do arquivo.
  const nameModalRef = useRef<ImperativeModalHandler>(null);
  const [fileNameInput, setFileNameInput] = useState('');
  const [suggestedName, setSuggestedName] = useState('');
  const [exportKind, setExportKind] = useState<'single' | 'combined'>('single');
  const [nameModalOpen, setNameModalOpen] = useState(false);

  useEffect(() => {
    dispatch(clearHistoric());
    dispatch(getHistoricsRequest());
  }, []);

  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 1.0,
    };

    const observer = new IntersectionObserver((entities) => {
      const target = entities[0];
      if (target.isIntersecting && !error) {
        dispatch(getHistoricsRequest());
      }
    }, options);

    if (loaderRef.current) {
      observer.observe(loaderRef.current);
    }
  }, [dispatch, error, hasNextPage, loading, selectedForm.form_id]);

  const handleEditResponse = (historic: Historic) => {
    dispatch(setSelectedHistoric(historic));
    dispatch(setAnswers(historic.answers));
    navigate('/field-notebook/historic/summary');
  };

  const enterSelectionMode = (index?: number) => {
    setSelectionMode(true);
    setSelectedIndexes(index !== undefined ? [index] : []);
  };

  const cancelSelectionMode = () => {
    setSelectionMode(false);
    setSelectedIndexes([]);
  };

  const toggleSelected = (index: number) => {
    setSelectedIndexes((prev) =>
      prev.includes(index)
        ? prev.filter((i) => i !== index)
        : [...prev, index],
    );
  };

  const openNameModal = () => {
    const count = selectedIndexes.length;
    if (count === 0) return;

    let suggested = '';
    if (count === 1) {
      const historic = historics[selectedIndexes[0]];
      setExportKind('single');
      suggested = buildIndividualPdfName({
        orgId: organization.id,
        formName: historic.form_name,
        formDate: historic.form_date,
      });
    } else {
      setExportKind('combined');
      suggested = buildCombinedPdfName({
        orgId: organization.id,
        orgName: organization.name,
      });
    }

    setSuggestedName(suggested);
    setFileNameInput(suggested);
    setNameModalOpen(true);
    nameModalRef.current?.open();
  };

  const confirmExport = useCallback(async () => {
    if (isExporting) return;

    const finalName = fileNameInput.trim()
      ? sanitizeFileName(fileNameInput)
      : suggestedName;

    setIsExporting(true);
    try {
      const { pdfColumnCount, logoUrl } = await getPdfTemplateConfig(
        organization.field_notebook_template_code,
      );

      if (exportKind === 'single') {
        const historic = historics[selectedIndexes[0]];
        TrackingService.sendEvent(
          'fieldnotebook_history_click_generatepdfbutton',
        );
        await generateAnswerPdf(
          historic,
          selectedForm,
          currentLanguage,
          pdfColumnCount,
          logoUrl,
          organization.name,
          finalName,
        );
      } else {
        const chosen = selectedIndexes.map((index) => historics[index]);
        TrackingService.watchGenericEvent(
          'historyscreen_click_combinePDFbutton',
        );
        await generateCombinedAnswerPdf(
          chosen,
          selectedForm,
          currentLanguage,
          pdfColumnCount,
          logoUrl,
          organization.name,
          finalName,
        );
      }

      nameModalRef.current?.close();
      setNameModalOpen(false);
      cancelSelectionMode();
    } catch (err) {
      console.error('Failed to export PDF', err);
    } finally {
      setIsExporting(false);
    }
  }, [
    isExporting,
    fileNameInput,
    suggestedName,
    exportKind,
    historics,
    selectedIndexes,
    selectedForm,
    currentLanguage,
    organization,
  ]);

  const selectedCount = selectedIndexes.length;

  return (
    <Container>
      <Title>{t('field_notebook')}</Title>
      <SubTitle>{t('historic_annotation')}</SubTitle>
      <Button
        leftIcon={<ArrowBackIcon />}
        onClick={() =>
          navigate(`/field-notebook/archives/${archiveId}/forms/historic`)
        }
      >
        {t('back')}
      </Button>
      <ToolsBar>
        <HistoricFilter />
        <HistoricExportButton disabled={!hasHistorics} loading={loading} />
      </ToolsBar>
      <SearchBar />
      {(!hasHistorics && !loading) || error ? (
        <>
          {error ? (
            <DontHaveArchivesContainer>
              <Typography
                style={{ color: themeOptions.palette.primary.main }}
                variant="h6"
              >
                {t('an_error_has_occured')}
              </Typography>
              <Typography style={{ color: themeOptions.palette.grey[500] }}>
                {t('unable_to_display_data')}
              </Typography>
              <Typography
                style={{
                  color: themeOptions.palette.grey[500],
                  marginBottom: 20,
                }}
              >
                {t('try_reload_page')}
              </Typography>
              <DontHaveArchivesImage />
              <Button
                variant="contained"
                rightIcon={<RefreshIcon />}
                onClick={handleLoadingArchives}
              >
                {t('dont_have_archives_button')}
              </Button>
            </DontHaveArchivesContainer>
          ) : (
            <DontHaveHistoricsContainer>
              <DontHaveHistoricImage />(
              <Typography style={{ color: themeOptions.palette.grey[500] }}>
                {t('dont_have_historics')}
              </Typography>
              )
            </DontHaveHistoricsContainer>
          )}
        </>
      ) : (
        <Timeline>
          {hasHistorics &&
            historics.map((historic, index) => (
              <TimelineItem key={index}>
                <TimelineSeparator>
                  <TimelineDot color="primary" />
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent>
                  <SelectableTile
                    selectable={selectionMode}
                    selected={selectedIndexes.includes(index)}
                    onClick={
                      selectionMode ? () => toggleSelected(index) : undefined
                    }
                  >
                    <Date>
                    <div>
                      {selectionMode && (
                        <Checkbox
                          checked={selectedIndexes.includes(index)}
                          onChange={() => toggleSelected(index)}
                          sx={{ p: 0, mr: 1 }}
                        />
                      )}
                      {t('register_by')} {historic.user_name} {t('on')}{' '}
                      {formatTimestampDate(historic.form_date, currentLanguage)}
                      {!selectionMode && (
                        <EditIcon onClick={() => handleEditResponse(historic)} />
                      )}
                      {historic.updated_by && historic.updated_at && (
                        <EditedText>
                          ({t('historic_edited_by')}: {historic.updated_by}{' '}
                          {formatTimestampDate(
                            historic?.updated_at,
                            currentLanguage,
                          )}
                          )
                        </EditedText>
                      )}
                    </div>
                    {!selectionMode && (
                      <DownloadPDFButton
                        type="button"
                        variant="text"
                        centerContent
                        onClick={() => enterSelectionMode(index)}
                      >
                        {t('generate_pdf')}
                        <DownloadPDFIcon color="primary" />
                      </DownloadPDFButton>
                    )}
                  </Date>
                    <HistoricContent historic={historic} />
                  </SelectableTile>
                  <Divider />
                </TimelineContent>
              </TimelineItem>
            ))}
          {loading && !error && (
            <>
              <TimelineItemSkeleton />
              <TimelineItemSkeleton />
            </>
          )}
          {hasNextPage && !loading && <span ref={loaderRef} />}
        </Timeline>
      )}

      {selectionMode && !nameModalOpen && (
        <SelectionBar>
          <Button
            variant="outlined"
            onClick={cancelSelectionMode}
            disabled={isExporting}
          >
            {t('cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={openNameModal}
            disabled={selectedCount === 0 || isExporting}
            isLoading={isExporting}
          >
            {selectedCount > 1
              ? `${t('combine_pdf')} (${selectedCount})`
              : t('export_pdf')}
          </Button>
        </SelectionBar>
      )}

      <Modal
        ref={nameModalRef}
        title={t('generate_pdf')}
        hasCloseIcon
        width={560}
        onClose={() => setNameModalOpen(false)}
      >
        <NameModalContent>
          <Typography className="explanation">
            {t('file_name_hint')} <strong>{suggestedName}</strong>
          </Typography>
          <TextField
            label={t('file_name')}
            value={fileNameInput}
            onChange={(event) => setFileNameInput(event.target.value)}
            fullWidth
            autoFocus
          />
          <Button
            variant="contained"
            onClick={confirmExport}
            disabled={isExporting}
            isLoading={isExporting}
            style={{ width: '100%' }}
          >
            {t('export_pdf')}
          </Button>
        </NameModalContent>
      </Modal>
    </Container>
  );
}
